// 游戏列表（Phase 4.1）：dense Table + 新建/删除。
// 视图层 GamesListView 解耦路由，方便 vitest mount。

import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { QueryView } from "@/components/ui/QueryView";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui-lite/Dialog";
import { Field, Input, Textarea } from "@/components/ui/Input";
import {
  listGames,
  upsertGame,
  type GameProfileSummary,
} from "@/api/games";

const NewGameSchema = z.object({
  game_id: z
    .string()
    .min(1, "game_id 必填")
    .regex(/^[a-zA-Z0-9_\-:.]+$/, "仅允许字母数字 _ - : ."),
  display_name: z.string().min(1, "显示名必填"),
  notes: z.string().optional(),
});
type NewGameForm = z.infer<typeof NewGameSchema>;

export const Route = createFileRoute("/games/")({
  component: GamesListRoute,
});

function GamesListRoute() {
  const navigate = useNavigate();
  return (
    <GamesListView onOpenGame={(id) => navigate({ to: "/games/$gameId", params: { gameId: id } })} />
  );
}

export interface GamesListViewProps {
  onOpenGame?: (gameID: string) => void;
}

export function GamesListView({ onOpenGame }: GamesListViewProps) {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);

  const q = useQuery({
    queryKey: ["games"],
    queryFn: listGames,
  });

  const createMut = useMutation({
    mutationFn: upsertGame,
    onSuccess: () => {
      toast.success("已创建 game profile");
      setCreateOpen(false);
      queryClient.invalidateQueries({ queryKey: ["games"] });
    },
    onError: (err) => toast.error(`创建失败：${(err as Error).message}`),
  });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          新建 game profile
        </Button>
      </div>

      <QueryView query={q} isEmpty={(d) => d.games.length === 0}>
        {(data) => (
          <table className="w-full border-collapse text-sm">
            <thead className="border-b border-[hsl(var(--border))] text-left text-xs text-[hsl(var(--muted-foreground))]">
              <tr>
                <th className="py-2 pr-3">游戏 ID</th>
                <th className="py-2 pr-3">显示名</th>
                <th className="py-2 pr-3">大陆</th>
                <th className="py-2 pr-3">地图</th>
                <th className="py-2 pr-3">账号</th>
                <th className="py-2 pr-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {data.games.map((g) => (
                <GameRow key={g.game_id} game={g} onOpen={onOpenGame} />
              ))}
            </tbody>
          </table>
        )}
      </QueryView>

      <Dialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="新建 game profile"
        description="game_id 必须唯一；创建后可编辑 global / protect / 大陆 / 地图。"
      >
        <NewGameFormView
          submitting={createMut.isPending}
          onSubmit={(values) => createMut.mutate(values)}
          onCancel={() => setCreateOpen(false)}
        />
      </Dialog>
    </div>
  );
}

function GameRow({
  game,
  onOpen,
}: {
  game: GameProfileSummary;
  onOpen?: (id: string) => void;
}) {
  return (
    <tr className="border-b border-[hsl(var(--border))]/60 align-top">
      <td className="py-1.5 pr-3 font-mono text-xs">
        {onOpen ? (
          <button
            type="button"
            onClick={() => onOpen(game.game_id)}
            className="hover:underline"
          >
            {game.game_id}
          </button>
        ) : (
          <Link
            to="/games/$gameId"
            params={{ gameId: game.game_id }}
            className="hover:underline"
          >
            {game.game_id}
          </Link>
        )}
      </td>
      <td className="py-1.5 pr-3">
        {onOpen ? (
          <button
            type="button"
            onClick={() => onOpen(game.game_id)}
            className="hover:underline"
          >
            {game.display_name || "—"}
          </button>
        ) : (
          <Link
            to="/games/$gameId"
            params={{ gameId: game.game_id }}
            className="hover:underline"
          >
            {game.display_name || "—"}
          </Link>
        )}
      </td>
      <td className="py-1.5 pr-3">{game.continent_count ?? 0}</td>
      <td className="py-1.5 pr-3">{game.map_count ?? 0}</td>
      <td className="py-1.5 pr-3">{game.account_count ?? 0}</td>
      <td className="py-1.5 pr-3">
        {onOpen ? (
          <Button size="sm" variant="outline" onClick={() => onOpen(game.game_id)}>
            打开
          </Button>
        ) : (
          <Link
            to="/games/$gameId"
            params={{ gameId: game.game_id }}
            className="text-xs underline"
          >
            打开
          </Link>
        )}
      </td>
    </tr>
  );
}

function NewGameFormView({
  submitting,
  onSubmit,
  onCancel,
}: {
  submitting: boolean;
  onSubmit: (values: NewGameForm) => void;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewGameForm>({
    // zod v4 + @hookform/resolvers v5：minor version 推断与 ZodObject 定义匹配
    // 在严格 mode 下有 false-positive，cast 为 never 走 resolver overload 2。
    resolver: zodResolver(NewGameSchema as never),
    defaultValues: { game_id: "", display_name: "", notes: "" },
  });

  return (
    <form className="space-y-3" onSubmit={handleSubmit(onSubmit)} noValidate>
      <Field label="game_id" htmlFor="game_id" error={errors.game_id?.message}>
        <Input id="game_id" autoComplete="off" {...register("game_id")} />
      </Field>
      <Field
        label="显示名"
        htmlFor="display_name"
        error={errors.display_name?.message}
      >
        <Input id="display_name" {...register("display_name")} />
      </Field>
      <Field label="备注" htmlFor="notes">
        <Textarea id="notes" rows={3} {...register("notes")} />
      </Field>
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          取消
        </Button>
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? "创建中…" : "创建"}
        </Button>
      </div>
    </form>
  );
}
